import { useMemo } from 'react';
import {
  FieldDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { Building2Icon, ShieldIcon, UsersIcon } from 'lucide-react';
import { useKnowledgeBasesList } from '../../model/useKnowledgeBasesList';
import type { SchemaWidgetProps } from '@/shared/ui/SchemaForm';
import { useTranslation } from 'react-i18next';

/** Sentinel for "no knowledge base": Radix reserves the empty string for "unset". */
// eslint-disable-next-line i18next/no-literal-string -- internal sentinel, never rendered
const NONE_VALUE = '__none__';

function KbIcon({ isInternal, hasWorkspace }: { isInternal: boolean; hasWorkspace: boolean }) {
  if (isInternal) return <ShieldIcon className="size-4 text-muted-foreground" />;
  if (hasWorkspace) return <UsersIcon className="size-4 text-muted-foreground" />;
  return <Building2Icon className="size-4 text-muted-foreground" />;
}

export function KbSelectWidget({
  id,
  label,
  value,
  onChange,
  schema,
  disabled,
  hasError,
}: SchemaWidgetProps) {
  const { t } = useTranslation('knowledge');

  const selectedValue = String(value ?? '');
  const { data: knowledgeBases = [] } = useKnowledgeBasesList(true);

  const sorted = useMemo(() => {
    return [...knowledgeBases].sort((a, b) => {
      // Foundation KBs first
      if (a.isInternal && !b.isInternal) return -1;
      if (!a.isInternal && b.isInternal) return 1;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [knowledgeBases]);

  // The list is scoped to the current workspace, so a config saved against
  // another one holds an id with no option to match. Radix renders the trigger
  // from the matching item, so without a stand-in row it would read as empty
  // while the form still submits that id.
  const isSelectionUnlisted = selectedValue !== '' && !sorted.some((kb) => kb.id === selectedValue);

  return (
    <div className="flex flex-col gap-1">
      <Select
        value={selectedValue || NONE_VALUE}
        onValueChange={(next) => onChange(next === NONE_VALUE ? undefined : next)}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          aria-label={label}
          error={hasError}
          aria-invalid={hasError || undefined}
        >
          <SelectValue placeholder={t('knowledgeBases.kbSelectWidget.none')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>{t('knowledgeBases.kbSelectWidget.none')}</SelectItem>
          {isSelectionUnlisted && <SelectItem value={selectedValue}>{selectedValue}</SelectItem>}
          {sorted.map((kb) => (
            <SelectItem key={kb.id} value={kb.id}>
              <KbIcon isInternal={kb.isInternal} hasWorkspace={Boolean(kb.workspaceId)} />
              {kb.name || kb.id}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {schema.description && <FieldDescription>{schema.description}</FieldDescription>}
    </div>
  );
}
